import { useEffect, useRef, useSyncExternalStore } from 'react'
import {
  getScoreSnapshot,
  isNodePending,
  subscribeToScoresStore,
  loadScores,
  acquireScoreChannel,
} from './confidenceScoresStore'
import type { ConfidenceScoreEntry } from './confidenceScoresStore'

export interface ConfidenceScoreResult {
  score: number | undefined
  isPending: boolean
  /** Full entry including dimensions + reasoning, for the detail modal. Undefined when untested. */
  scoreEntry: ConfidenceScoreEntry | undefined
}

// ── Hook ──────────────────────────────────────────────────────────────────────

/**
 * Returns the confidence score, pending state, and full score entry for a specific node.
 *
 * IMPORTANT: useSyncExternalStore requires getSnapshot to return a stable reference
 * when nothing changed. We cache the last result in a ref and return the same object
 * when primitive values are identical — otherwise React detects a new object on every
 * call and enters an infinite re-render loop.
 */
export function useConfidenceScore(
  nodeId: string,
  mindmapId: string | undefined
): ConfidenceScoreResult {
  useEffect(() => {
    if (!mindmapId) return

    // Trigger initial fetch (idempotent — no-op if already fetched/fetching)
    void loadScores(mindmapId)

    // Acquire shared channel subscription — ref-counted per mindmap
    return acquireScoreChannel(mindmapId)
  }, [mindmapId])

  // Stable cache: return same object reference when nothing changed so
  // useSyncExternalStore doesn't trigger an infinite render loop.
  const lastResult = useRef<ConfidenceScoreResult>({
    score: undefined,
    isPending: false,
    scoreEntry: undefined,
  })

  const result = useSyncExternalStore(subscribeToScoresStore, () => {
    const entry = mindmapId ? getScoreSnapshot(nodeId, mindmapId) : undefined
    const pending = mindmapId ? isNodePending(nodeId, mindmapId) : false
    const score = entry?.score

    const prev = lastResult.current
    if (prev.score === score && prev.isPending === pending && prev.scoreEntry === entry) {
      return prev
    }

    const next: ConfidenceScoreResult = { score, isPending: pending, scoreEntry: entry }
    lastResult.current = next
    return next
  })

  return result
}
